import { useId, useState } from 'react';
import { Link } from 'react-router';
import { MAX_ACTIVE_MEMBERS, MAX_GROUPS_PER_CREATOR } from '../../core/access';
import { MAX_INVITE_ADDRESSES, type AddressRefusal } from '../../core/invitations';
import { createGroup, createInvitation, type GetToken, type GroupSummary } from '../api';
import { groupsPath } from '../routes';
import {
  fanOut, INVITE_CONCURRENCY, planInvites, settleInvite, type InviteResult, type SkippedAddress,
} from './InviteResults';
import type { FailedInvite, StartedGroup, StartedInvite } from './StartedGroupPanel';

/**
 * Starting a group, at an address of its own: `/new`.
 *
 * It used to be a name field under the group list, and inviting was a separate act inside a
 * screen the teacher had not seen yet. That is two screens for one intention — "I want to talk
 * about this with Marta and Sam" — and the second half was easy never to do. This screen is
 * the whole intention: the name, and the colleagues, in one submit.
 *
 * Its shape is `MaterialUploadPage`'s, deliberately: a real address that survives a cold load
 * and a refresh, a toolbar saying what the screen is and how to leave it, one form, and a
 * landing that shows the result rather than returning to where you started.
 *
 * **The partial failure is the design.** Creating the group and creating N invitations are
 * separate requests with no transaction across them, so three outcomes are real and each has
 * its own answer:
 *
 * - **The group is not created.** Nothing else was attempted. The teacher stays here with
 *   their name and every address still on the list, and the refusal says which wall they hit —
 *   the per-creator cap, an empty name, the rate limiter, or a network that was not there.
 * - **The group is created and every invitation lands.** Into the room, with the links.
 * - **The group is created and some invitations do not.** Still into the room — the group
 *   exists and pretending otherwise is how a teacher creates it twice — carrying the addresses
 *   that were refused, named one by one and retryable one by one (`StartedGroupPanel`).
 *
 * Addresses are validated *before* anything is sent, by the same `core` function the Worker
 * decides with. That is a courtesy and not a boundary: the Worker refuses these addresses too,
 * and would refuse them if this file vanished. What it buys is that a teacher does not type
 * five addresses, wait, and then learn the second one had a space in it.
 *
 * The field takes a pasted list as readily as one address (R1): the good ones join the list
 * and the skipped ones stay in the field, each named with its reason, so a typo is fixed where
 * it was typed rather than retyped from memory. The invitations go out four at a time (KTD4),
 * and each one's email result rides into the room with it (R2, R4).
 */

export interface NewGroupPageProps {
  getToken: GetToken;
  /**
   * This teacher's own address, as `/api/me` reported it, or null when it did not.
   *
   * Used for one refusal: they are the group's admin from the instant it exists, so inviting
   * themselves is a link that can only ever bounce. Null refuses nothing — an address we do
   * not know is not a match, and the Worker is the one that decides.
   */
  selfEmail: string | null;
  /** Where a created group lands. Given the group, and whatever came of its invitations. */
  onCreated: (group: GroupSummary, started: StartedGroup) => void;
  /** Out of here without creating anything: back to the groups list. */
  onCancel: () => void;
}

const ADD_REFUSAL_COPY: Readonly<Record<AddressRefusal, string>> = {
  invalid: 'That does not look like an email address.',
  self: 'That is your own address. You are this group’s admin the moment it exists.',
  duplicate: 'That address is already on the list.',
  list_full:
    `A group holds ${MAX_ACTIVE_MEMBERS} people and you are one of them, so ${MAX_INVITE_ADDRESSES} `
    + 'is as many as you can invite at once. Remove one to add another.',
};

/** What went wrong creating the group itself. Nothing was created in any of these cases. */
const creationCopy = (status: number, error: string, reason?: string): string => {
  if (error === 'group_limit_reached') {
    return `You have started ${MAX_GROUPS_PER_CREATOR} groups, which is as many as Collab allows. `
      + 'Leave one to start another. Nothing was created, and your name and addresses are still here.';
  }
  if (reason === 'invalid_name') return 'Give the group a name.';
  if (error === 'rate_limited') {
    return 'That is a lot of groups in a short time. Wait a minute and try again. Nothing was created.';
  }
  if (status === 0) {
    return 'We could not reach Collab. Nothing was created — your name and your addresses are still here.';
  }
  return 'We could not create that group. Try again in a moment. Nothing was created.';
};

/** The per-address answers, sorted into what the room's panel carries. Order is kept. */
function startedFrom(results: readonly InviteResult[]): StartedGroup {
  const created: StartedInvite[] = [];
  const members: string[] = [];
  const failed: FailedInvite[] = [];
  for (const result of results) {
    if (result.state === 'invited') created.push({ invitation: result.invitation, emailResult: result.emailResult });
    else if (result.state === 'already_member') members.push(result.email);
    else if (result.state === 'failed') failed.push({ email: result.email, reason: result.reason });
  }
  return { created, members, failed };
}

export default function NewGroupPage({
  getToken, selfEmail, onCreated, onCancel,
}: NewGroupPageProps) {
  const [name, setName] = useState('');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  /** What the last Add left out, and why. Empty when it took everything. */
  const [skipped, setSkipped] = useState<SkippedAddress[]>([]);
  const [busy, setBusy] = useState(false);
  /** What the submit is doing right now. Null when it is not running. */
  const [progress, setProgress] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const nameId = useId();
  const addressId = useId();

  /**
   * Put what was typed — one address or a pasted list — on the list.
   *
   * Returns the list it produced rather than only setting state, because submit calls this for
   * whatever is still sitting in the field and then has to send *that* list — a `useState`
   * setter has not landed by the next line. Null when anything was skipped: submit must not
   * create a group behind an address the teacher meant and Collab dropped.
   */
  function add(raw: string): string[] | null {
    const plan = planInvites(raw, selfEmail, addresses);
    const next = [...addresses, ...plan.emails];
    setAddresses(next);
    // Nothing taken, the field is left exactly as typed. Something taken, what remains is only
    // what was skipped, so the field is the to-do list.
    if (plan.emails.length > 0) setDraft(plan.skipped.map((entry) => entry.email).join(', '));
    setSkipped(plan.skipped);
    return plan.skipped.length === 0 ? next : null;
  }

  function remove(email: string) {
    setAddresses((current) => current.filter((entry) => entry !== email));
    setSkipped([]);
  }

  async function submit() {
    const trimmed = name.trim();
    if (busy || trimmed.length === 0) return;

    /*
     * An address typed but not added is still an address the teacher meant to invite. Adding
     * it here rather than dropping it is the difference between "I invited three people" and
     * "I invited two and Collab said nothing about the third". If it is refused, the refusal
     * is shown and nothing is sent — the group is not created behind a mistake.
     */
    let invitees = addresses;
    if (draft.trim().length > 0) {
      const next = add(draft);
      if (next === null) return;
      invitees = next;
    }

    setBusy(true);
    setFailure(null);
    setProgress('Creating the group…');

    const group = await createGroup(getToken, trimmed);
    if (!group.ok) {
      // Nothing was created and nothing was sent. Everything the teacher typed stays put.
      setBusy(false);
      setProgress(null);
      setFailure(creationCopy(group.status, group.error, group.reason));
      return;
    }

    const groupId = group.data.group.id;

    // Four at a time (KTD4), each answer stored at its address's place in the list, so the
    // results come out in the order the teacher typed them however the requests interleave.
    const results: InviteResult[] = [];
    let settled = 0;
    setProgress(`Inviting 0 of ${invitees.length}…`);
    await fanOut([...invitees.entries()], INVITE_CONCURRENCY, async ([index, email]) => {
      results[index] = settleInvite(email, await createInvitation(getToken, groupId, email));
      settled += 1;
      setProgress(`Inviting ${settled} of ${invitees.length}…`);
    });

    // The group exists. Whatever happened to the invitations, this teacher goes into it.
    onCreated(group.data.group, startedFrom(results));
  }

  return (
    <section aria-label="New group">
      <div className="toolbar">
        <div className="toolbar-title">
          <h2>New group</h2>
          <p className="meta">A private group, and the colleagues you want in it.</p>
        </div>
        <div className="row-actions">
          <Link className="button-link" to={groupsPath()}>Back to your groups</Link>
        </div>
      </div>

      <section className="panel" aria-label="Start a group">
        {/*
          * `noValidate`, deliberately. `type="email"` is here for the keyboard a phone puts up,
          * not for the browser's own refusal: left on, a half-typed address in the field would
          * block the submit with an untranslatable native bubble *instead of* the sentence this
          * page writes — and the teacher would never learn that the address was the problem.
          * Every refusal on this screen is one we chose the words for.
          */}
        <form
          className="upload-form"
          noValidate
          onSubmit={(event) => { event.preventDefault(); void submit(); }}
        >
          <label htmlFor={nameId}>Group name</label>
          <input
            id={nameId}
            className="collab-group-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Eighth grade science"
            disabled={busy}
            required
          />

          {/*
            * `collab-invite-dialog` is a replay block selector (`analytics/client.ts`): from here
            * down to the end of the list, what is on screen is colleagues' addresses.
            */}
          <div className="collab-invite-dialog">
            <label htmlFor={addressId}>Invite a colleague</label>
            <p className="hint" id={`${addressId}-hint`}>
              Add as many as you like, or none — paste a list if you have one. You can invite
              people later from inside the group.
            </p>
            <div className="address-entry">
              <input
                id={addressId}
                type="email"
                value={draft}
                aria-describedby={`${addressId}-hint`}
                onChange={(event) => { setDraft(event.target.value); setSkipped([]); }}
                onKeyDown={(event) => {
                  // Enter adds the address. It must not submit the form: a teacher halfway
                  // through a list has not finished typing it.
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  if (draft.trim().length > 0) add(draft);
                }}
                placeholder="colleague@school.org"
                disabled={busy}
              />
              <button
                type="button"
                className="address-add"
                disabled={busy || draft.trim().length === 0}
                onClick={() => add(draft)}
              >
                Add
              </button>
            </div>

            {/* One problem is one sentence; the address is still in the field above it. */}
            {skipped.length === 1 && (
              <p className="error" role="alert">{ADD_REFUSAL_COPY[skipped[0]!.reason]}</p>
            )}
            {/* A paste with several problems names each one, by what was typed. */}
            {skipped.length > 1 && (
              <div className="error" role="alert">
                <p>{skipped.length} addresses were left out:</p>
                <ul>
                  {skipped.map((entry, index) => (
                    <li key={`${index}:${entry.email}`}>{entry.email} — {ADD_REFUSAL_COPY[entry.reason]}</li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              * The list, announced as it changes. A teacher who adds an address with the keyboard
              * gets no visual confirmation they can hear, so the count is a live region and the
              * list underneath it is what the count is about.
              */}
            <div aria-live="polite">
              <p className="meta" role="status">
                {addresses.length === 0
                  ? 'No colleagues to invite yet.'
                  : `${addresses.length} ${addresses.length === 1 ? 'colleague' : 'colleagues'} to invite.`}
              </p>
              {addresses.length > 0 && (
                <ul className="address-list">
                  {addresses.map((email) => (
                    <li key={email}>
                      <span>{email}</span>
                      {/*
                        * The visible word is "Remove"; the accessible name says which one, so a
                        * screen reader hears twenty distinguishable buttons rather than twenty
                        * called "Remove". The visible text is a prefix of the accessible name,
                        * which is what keeps voice control able to say "Remove".
                        */}
                      <button
                        type="button"
                        className="secondary"
                        aria-label={`Remove ${email}`}
                        disabled={busy}
                        onClick={() => remove(email)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {failure && <p className="error" role="alert">{failure}</p>}

          <div className="form-actions">
            <button type="submit" disabled={busy || name.trim().length === 0}>
              {busy ? 'Creating…' : 'Create group'}
            </button>
            <button type="button" className="secondary" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
            {progress && <span className="hint" role="status">{progress}</span>}
          </div>
        </form>
      </section>
    </section>
  );
}
